import { Chip, styled, Tooltip, useTheme } from '@ethings/ui-toolkit';
import { useMediaQuery } from '@mui/material';
import { FC, type JSX } from 'react';
import { ResponsiveText } from '../responsive-text';

interface Props {
  align?: 'left' | 'center' | 'right';
  background?: string;
  color?: string;
  'data-testid'?: string;
  fullWidth?: boolean;
  icon?: JSX.Element;
  label?: string;
  tooltip?: string;
  subtitle?: string;
  variant?: 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption';
}

interface StyledChipProps {
  chipAlign?: 'left' | 'center' | 'right';
  chipBackground?: string;
  chipColor?: string;
  chipFullWidth?: boolean;
}

export const LabelledBadge: FC<Props> = ({ align, background, color, tooltip = '', fullWidth, subtitle, label, variant = 'subtitle2', ...props }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const StyledChip = styled(Chip)<StyledChipProps>(({ theme, chipAlign, chipBackground, chipColor, chipFullWidth }) => ({
    backgroundColor: chipBackground || theme.palette.primary.main,
    color: chipColor || theme.palette.primary.contrastText,
    width: chipFullWidth ? '100%' : 'auto',
    borderRadius: theme.spacing(1),
    height: 'auto',
    justifyContent: chipAlign || 'center',
    textAlign: chipAlign || 'center',
    minHeight: 32,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    fontWeight: '500',
    '.MuiChip-icon': {
      color: 'inherit',
      fontSize: isSmallScreen ? '0.875rem' : '1rem',
    },
  }));

  return (
    <Tooltip arrow title={tooltip}>
      <StyledChip
        chipAlign={align}
        chipBackground={background}
        chipColor={color}
        chipFullWidth={fullWidth}
        label={
          <>
            <ResponsiveText
              component='div'
              variant={variant}
              align='inherit'
              responsiveOptions={{
                minFontSize: '0.75rem',
                maxFontSize: '0.875rem',
                textLengthThreshold: 20,
              }}
            >
              {label}
            </ResponsiveText>
            {subtitle && (
              <ResponsiveText
                variant="caption"
                responsiveOptions={{
                  minFontSize: '0.65rem',
                  maxFontSize: '0.75rem',
                  textLengthThreshold: 25,
                }}
                style={{ opacity: 0.8 }}
              >
                {subtitle}
              </ResponsiveText>
            )}
          </>
        } {...props} />
    </Tooltip>
  );
};
